// import React, { useState } from "react";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { Tab } from "@mui/material";
import { useRef } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { ObjectFeatures2Enum } from "../../common/enums/ObjectFeatures2Enum";
import { ActionMenuItem, BaseActionEnum } from "../../components/action-menu/ActionMenuItem";
import AlarmSetpointList, { AlarmSetpointListType } from "../../components/alarm-setpoint-list/AlarmSetpointList";
import { ContentView } from "../../components/content-view/ContentView";
import ContextualAppBar from "../../components/contextual-app-bar/ContextualAppBar";
import ObjectAttachments, { ObjectAttachmentsType } from "../../components/object-attachments/ObjectAttachments";
import ObjectExternalContent, { ObjectExternalContentType } from "../../components/object-external-content/ObjectExternalContent";
import ObjectProperties, { ObjectPropertiesType } from "../../components/object-properties/ObjectProperties";
import ObjectRelationships, { ObjectRelationshipsType } from "../../components/object-relationships/ObjectRelationships";
import ObjectTagValues, { ObjectTagValuesType } from "../../components/object-tag-values/ObjectTagValues";
import { ObjectTagValuesMode } from '../../components/object-tag-values/ObjectTagValuesMode';
import { RunTicketListType } from "../../components/run-ticket-list/RunTicketList";
import TabBar from "../../components/tab-bar/TabBar";
import TabContainer from "../../components/tab-container/TabContainer";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useAvailableObjectDetailsTabs } from "../../queries/available-views/UseAvailableObjectDetailsTabs";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { ObjectDetailsViewTabEnum, useObjectDetailsViewState } from "../../states/object-details-view-state/ObjectDetailsViewState";
import ObjectHistoryTabContent, { ObjectHistoryTabContentType } from "../../components/object-history/ObjectHistoryTabContent";
import ObjectHistoryContextProvider from "../../components/object-history/ObjectHistoryContext";

export default function WellDetailsView() {
    const { t } = useTranslation();
    const {
        activeTab,
        setActiveTab,
        actions,
        setActions,
        resetActions,
        viewOptionState,
        setViewOptionState
    } = useObjectDetailsViewState(eLynxObjectTypeEnum.Well, /*false*/);

    const { data: availableTabs } = useAvailableObjectDetailsTabs({ enabled: true });

    const objectTagValues = useRef<ObjectTagValuesType>(undefined);
    const runTicketList = useRef<RunTicketListType>(undefined);
    const alarmSetpointList = useRef<AlarmSetpointListType>(undefined);
    const objectHistory = useRef<ObjectHistoryTabContentType>(null);
    const objectAttachments = useRef<ObjectAttachmentsType>(undefined);
    const objectRelationships = useRef<ObjectRelationshipsType>(undefined);
    const objectExternalContent = useRef<ObjectExternalContentType>(undefined);
    const objectProperties = useRef<ObjectPropertiesType>(undefined);
    const isPhone = isMobile && !isTablet;
    const [offlineState] = useOfflineDetectContext();
    const routeContext = useRouteContext();    
    const [userState] = useUserContext();


    const { data: objectViewState } = useQueryNotifications(useObjectViewState)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: {
            id: routeContext.contextObject?.id as number,
            objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum
        },
        offline: offlineState.offline,
        enabled: !offlineState.offline
    });

    const hasExternalConent = ((objectViewState?.features2 ?? ObjectFeatures2Enum.None) & ObjectFeatures2Enum.AdditionalContent) > 0 &&
                                objectViewState?.externalContent?.url;



    function onExecuteAction(item: ActionMenuItem) {

        switch (item.action) {

            case BaseActionEnum.Refresh:

                if (activeTab === ObjectDetailsViewTabEnum.Values) {
                    objectTagValues.current?.executeAction(item.action);
                } else if (activeTab === ObjectDetailsViewTabEnum.History) {
                    objectHistory.current?.executeAction(item.action);
                } else if (activeTab === ObjectDetailsViewTabEnum.RunTickets) {
                    runTicketList.current?.executeAction(item.action);
                } else if (activeTab === ObjectDetailsViewTabEnum.AlarmSetpoints) {
                    alarmSetpointList.current?.executeAction(item.action);
                } else if (!isPhone && !offlineState.offline && activeTab === ObjectDetailsViewTabEnum.Attachments) {
                    objectAttachments.current?.executeAction(item.action);
                } else if (!isPhone && !offlineState.offline && activeTab === ObjectDetailsViewTabEnum.Relationships) {
                    objectRelationships.current?.executeAction(item.action);
                }

                break;

            case BaseActionEnum.DemandScan:
            case BaseActionEnum.WriteDown:

                setActiveTab(ObjectDetailsViewTabEnum.Values);

                objectTagValues.current?.executeAction(item.action);

                break;

            case BaseActionEnum.AddMemo:
            case BaseActionEnum.AddDiagnositicEvent:
                setActiveTab(ObjectDetailsViewTabEnum.History);

                objectHistory.current?.executeAction(item.action);

                break;

            case BaseActionEnum.AddRunTicket:
                setActiveTab(ObjectDetailsViewTabEnum.RunTickets);

                runTicketList.current?.executeAction(item.action);

                break;

            case BaseActionEnum.AddAttachment:
                setActiveTab(ObjectDetailsViewTabEnum.Attachments);

                objectAttachments.current?.executeAction(item.action);

                break;


            case BaseActionEnum.AddAttribute:
                setActiveTab(ObjectDetailsViewTabEnum.Properties);

                objectProperties.current?.executeAction(item.action);

                break;
    

        }
    }

    return (
        <ContentView>

            <ContextualAppBar actions={actions}
                onExecuteAction={onExecuteAction}
                viewOptionState={viewOptionState}
            />

            <TabBar
                value={activeTab}
                onChange={(_event, value) => setActiveTab(value)}
            >
                {availableTabs?.hasValues &&
                    <Tab label={t("objectDetails.tabs.tagValues")} value={ObjectDetailsViewTabEnum.Values} />
                }

                {availableTabs?.hasProperties &&
                    <Tab label={t("objectDetails.tabs.properties")} value={ObjectDetailsViewTabEnum.Properties} />
                }

                {availableTabs?.hasAlarmSetpoints &&
                    <Tab label={t("objectDetails.tabs.alarmSetpoints")} value={ObjectDetailsViewTabEnum.AlarmSetpoints} />
                }

                {!isPhone && !offlineState.offline && availableTabs?.hasAttachments &&
                    <Tab label={t("objectDetails.tabs.attachments")} value={ObjectDetailsViewTabEnum.Attachments} />
                }

                {availableTabs?.hasHistory &&
                    <Tab label={t("objectDetails.tabs.history")} value={ObjectDetailsViewTabEnum.History} />
                }

                {!isPhone && !offlineState.offline && availableTabs?.hasRelationships &&
                    <Tab label={t("objectDetails.tabs.relationships")} value={ObjectDetailsViewTabEnum.Relationships} />
                }

                {hasExternalConent &&
                    <Tab label={objectViewState?.externalContent?.title ?? ''} value={ObjectDetailsViewTabEnum.ExternalContent} />
                }


            </TabBar>

            {availableTabs?.hasValues &&
                <TabContainer visible={activeTab === ObjectDetailsViewTabEnum.Values}>
                    <ObjectTagValues
                        mode={ObjectTagValuesMode.Values}
                        grouped={true}
                        actions={actions}
                        setActions={setActions}
                        resetActions={resetActions}
                        setViewOptionState={setViewOptionState}
                        ref={objectTagValues}
                    />
                </TabContainer>
            }

            {availableTabs?.hasProperties &&
                <TabContainer visible={activeTab === ObjectDetailsViewTabEnum.Properties}>
                    <ObjectProperties ref={objectProperties} />
                </TabContainer>
            }

            {availableTabs?.hasAlarmSetpoints &&
                <TabContainer visible={activeTab === ObjectDetailsViewTabEnum.AlarmSetpoints}>
                    <AlarmSetpointList
                        ref={alarmSetpointList}
                        setViewOptionState={setViewOptionState}
                    />
                </TabContainer>
            }

            {!isPhone && !offlineState.offline && availableTabs?.hasAttachments &&
                <TabContainer visible={activeTab === ObjectDetailsViewTabEnum.Attachments}>
                    <ObjectAttachments
                        setViewOptionState={setViewOptionState}
                        ref={objectAttachments}
                    />
                </TabContainer>
            }

            {availableTabs?.hasHistory &&
                <ObjectHistoryContextProvider>
                    <TabContainer visible={activeTab === ObjectDetailsViewTabEnum.History}>
                        <ObjectHistoryTabContent
                            ref={objectHistory}
                            setViewOptionState={setViewOptionState}
                        />
                    </TabContainer>
                </ObjectHistoryContextProvider>
            }

            {availableTabs?.hasRelationships &&
                <TabContainer visible={activeTab === ObjectDetailsViewTabEnum.Relationships}>
                    <ObjectRelationships
                        ref={objectRelationships}
                        setViewOptionState={setViewOptionState}
                    />
                </TabContainer>
            }

            {hasExternalConent &&
                <TabContainer visible={activeTab === ObjectDetailsViewTabEnum.ExternalContent}>
                    <ObjectExternalContent
                        ref={objectExternalContent}
                    />
                </TabContainer>
            }

        </ContentView>
    );
}
